/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {
  Box,
  CircularProgress,
  type CircularProgressProps,
  Link as MLink,
  Link as MuiLink,
  Skeleton,
  Stack,
  type SxProps,
  type Theme,
  Tooltip,
  Typography,
  type TypographyProps,
} from '@mui/material';
import type { AppURL } from '@poweralpha/common';
import { merge } from 'lodash';
import { isValidElement, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Checkmark from './Checkmark';

const summaryItemStyleSx = {
  textAlign: 'left',
  border: 0,
  borderLeft: 5,
  borderColor: '#2E96FF',
  borderStyle: 'solid',
  pl: '5px',
  mb: 1,
};

const longNameStyleSx = {
  whiteSpace: 'nowrap',
  width: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'block',
};

interface SummaryItemValueProps {
  value: React.ReactNode;
  fontSize: number;
  valueTypographyProps?: TypographyProps;
  linkUrl?: string;
  postfixUnits?: string;
}

function SummaryItemValue({
  value,
  valueTypographyProps,
  fontSize,
  linkUrl,
  postfixUnits,
}: Readonly<SummaryItemValueProps>) {
  const typographyProps = valueTypographyProps ?? {
    variant: 'h6',
    sx: { ...longNameStyleSx, fontSize },
  };
  const valueElement = (
    <Typography noWrap {...typographyProps}>
      {[undefined, null, ''].includes(value as string) ? '-' : value}
      {postfixUnits ?? ''}
    </Typography>
  );
  return linkUrl ? (
    <MLink component={Link} to={linkUrl}>
      {valueElement}
    </MLink>
  ) : (
    valueElement
  );
}

interface SummaryItemProps extends Omit<SummaryItemValueProps, 'fontSize'> {
  label: React.ReactNode;
  hide?: boolean;
  hideIfNullish?: boolean;
  fontSize?: 'small' | 'medium' | 'large';
  sx?: SxProps<Theme>;
  minWidth?: number | string;
  color?: string;
}

export function SummaryItem({
  value,
  label,
  linkUrl,
  postfixUnits,
  hide,
  hideIfNullish,
  fontSize,
  sx,
  minWidth,
  color,
}: Readonly<SummaryItemProps>) {
  if (hide || (hideIfNullish && [undefined, null, '', 0].includes(value as string))) {
    return <div />;
  }

  let baseFontSize: number;
  switch (fontSize) {
    case 'large':
      baseFontSize = 14;
      break;
    case 'medium':
      baseFontSize = 12;
      break;
    default:
      baseFontSize = 10;
      break;
  }
  const summarySx = merge(
    {},
    summaryItemStyleSx,
    sx ?? {},
    minWidth !== undefined ? { minWidth } : {},
    color ? { color } : {},
  );

  return (
    <Tooltip
      title={
        <Box>
          <Typography variant="subtitle2">{value}</Typography>
          {label}
        </Box>
      }
    >
      <Box sx={summarySx}>
        <SummaryItemValue
          value={value}
          fontSize={baseFontSize + 2}
          linkUrl={linkUrl}
          postfixUnits={postfixUnits}
        />
        <Typography
          variant="subtitle2"
          noWrap
          sx={{
            fontSize: baseFontSize,
            color: 'text.secondary',
          }}
        >
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
}

const defaultValueTypographyProps: TypographyProps = {
  variant: 'h6',
  sx: {
    fontSize: 16,
    lineHeight: 1,
  },
};

export interface InputKeyValueProps {
  value: React.ReactNode;
  label: React.ReactNode;
  postfixUnits?: string;
  hideIfNullish?: boolean;
  linkRef?: (id: string) => { path: AppURL; name: string };
  valueTypographyProps?: TypographyProps;
  hide?: boolean;
  isLoading?: boolean;
  alignRight?: boolean;
  sx?: SxProps<Theme>;
}

function InputKeyValue({
  value,
  label,
  hideIfNullish,
  postfixUnits,
  linkRef,
  hide,
  isLoading,
  valueTypographyProps,
  alignRight,
  sx,
}: Readonly<InputKeyValueProps>) {
  const renderValue = useMemo(() => {
    if (isLoading) {
      return <Skeleton width="100%" />;
    }

    const typographyProps = merge(
      {},
      defaultValueTypographyProps,
      valueTypographyProps ?? {},
      alignRight ? { align: 'right' } : {},
    );
    const typographySx = merge({}, typographyProps.sx ?? {});
    if (linkRef && typeof value === 'string') {
      const { path, name } = linkRef(value);
      return (
        <MuiLink
          component={Link}
          to={path}
          target="_blank"
          noWrap
          {...typographyProps}
          sx={merge({}, typographySx, { color: 'text.primary' })}
        >
          {name}
        </MuiLink>
      );
    }

    if (typeof value === 'boolean') {
      return <Checkmark checked={value} sx={{ height: 18, width: 18 }} />;
    }

    return (
      <Typography component="div" noWrap {...typographyProps}>
        {[undefined, null, ''].includes(value as string) ? '-' : value}
        {`${postfixUnits || ''}`}
      </Typography>
    );
  }, [value, linkRef, postfixUnits, valueTypographyProps, isLoading, alignRight]);

  const returnNull = useMemo(() => {
    if (hide) return true;
    if (!hideIfNullish) return false;
    if (isValidElement(value)) {
      const innerVal =
        value.props && typeof value.props === 'object' && 'value' in value.props
          ? value.props.value
          : value;
      return innerVal === undefined || innerVal === null || innerVal === '';
    }
    return value === undefined || value === null || value === '';
  }, [value, hide, hideIfNullish]);

  if (returnNull) {
    return null;
  }

  return (
    <Stack
      sx={{
        mb: 0.5,
        p: '4px',
        ...sx,
      }}
    >
      {renderValue}
      <Typography
        component="div"
        variant="body1"
        noWrap
        sx={{
          color: 'text.secondary',
          fontSize: 10,
          lineHeight: 1,
          mt: 0.3,
          // only override when right-aligned; leave default consumers' alignment untouched
          ...(alignRight ? { textAlign: 'right' } : {}),
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

export default InputKeyValue;

export function CircularPercentage(
  props: Omit<CircularProgressProps, 'color'> & { value: number; color: string; label?: string },
) {
  const { color, value, label, sx, ...rest } = props;

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress
        variant="determinate"
        value={value}
        sx={{ ...(sx ?? {}), color }}
        {...rest}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
          {label ?? `${Math.round(value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}
